import { useCallback, useContext, useEffect, useState } from "react";
import { deleteAddon, postAddon } from "@modules/eirSalesFacade/apis/addons";
import { useFetchState } from "@hooks/useFetchState";
import { mapNewCartAddonIdList } from "@features/cart/components/BroadbandCard/BroadbandCartAddon/utils";
import { ICartOffer } from "@features/cart/ICart";
import { useScroller } from "@hooks/useScroller";
import { BUTTON_IDS } from "@features/dataLayer/buttonIds";
import { getCartItemsNumber } from "@utils/getCartItemsNumber";
import { NumberOfferContext } from "../../App";

interface IParams {
    catalogCode: string;
    broadbandCartOffer: ICartOffer;
    isSidePanel?: boolean;
}

export const useBroadbandAddon = (props: IParams) => {
    const { catalogCode, broadbandCartOffer, isSidePanel } = props;

    const { setNumberOfferSelected } = useContext(NumberOfferContext);

    const { scrollToComponent } = useScroller();
    const { startFetching, endFetching, isLoading, error } = useFetchState();

    const broadbandCartOfferId = broadbandCartOffer.offerId;
    const hasAddonInCart = broadbandCartOffer.addOns.length > 0;
    const addonIdInCartList = broadbandCartOffer.addOns.map((addon) => addon.id);

    const [isCounterVisible, setIsCounterVisible] = useState<boolean>(hasAddonInCart);
    const [addonIdList, setAddonIdList] = useState<number[]>(addonIdInCartList);

    const addAddon = useCallback(async () => {
        try {
            startFetching();
            const newCart = await postAddon(broadbandCartOfferId, { catalogCode });

            if (newCart) {
                const newAddonIdList = mapNewCartAddonIdList(newCart);
                setAddonIdList(newAddonIdList);
                setNumberOfferSelected(getCartItemsNumber(newCart));

                if (!isCounterVisible) {
                    setIsCounterVisible(true);
                }
            }

            endFetching();
        } catch (apiError) {
            endFetching(apiError);
        }
    }, [startFetching, broadbandCartOfferId, catalogCode, endFetching, setNumberOfferSelected, isCounterVisible]);

    const removeAddon = useCallback(async () => {
        try {
            startFetching();

            const newCart = await deleteAddon(broadbandCartOfferId, addonIdList[0]);

            if (newCart) {
                const newAddonIdList = mapNewCartAddonIdList(newCart);
                setAddonIdList(newAddonIdList);
                setNumberOfferSelected(getCartItemsNumber(newCart));
            }

            endFetching();
        } catch (apiError) {
            endFetching(apiError);
        }
    }, [startFetching, broadbandCartOfferId, addonIdList, endFetching, setNumberOfferSelected]);

    useEffect(() => {
        if (!isCounterVisible) {
            const buttonId = isSidePanel
                ? BUTTON_IDS.MOBILE_FLOW.DRAWER_CTA.BOOST_MY_BROADBAND
                : BUTTON_IDS.BROADBAND_FLOW.ADD_TO_CART_CTA.BOOST_MY_BROADBAND;

            scrollToComponent(buttonId);
        }
    }, [isCounterVisible, isSidePanel, scrollToComponent]);

    return {
        handleAddAddon: addAddon,
        handleRemoveAddon: removeAddon,
        counter: addonIdList?.length ?? 0,
        isCounterVisible,
        isLoading,
        error,
    };
};
